// src/features/items/components/ItemBulkImport.js
import React, { useCallback, useMemo, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Alert,
    Row,
    Col,
    Input,
} from "reactstrap";
import _ from "lodash";

import { importItems, importAndSaveItems } from "../data/importExportItemsService";
import { createAudit } from "../../../features/audit/data/auditService";
import { formatDateTime } from "../../../utils/dateUtils";
import { requiredExcelFileExtensions } from "../../../utils/excelUtils";

import { useQuery } from "@tanstack/react-query";
import { fetchItemLookupBatch } from "../data/itemLookupService";

// Use your table component
import DataTable from "../../../components/DataTable";
import { fetchItemLookupType } from "../data/itemLookupTypeService";
import { fetchTags } from "../../tags/data/tagService";
import { fetchSuppliers } from "../../suppliers/data/supplierService";

const ItemBulkImport = ({
    isOpen,
    onClose,
    onImported, // callback to let parent refresh
    title = "Import Items",
    requiredFileExtensions = requiredExcelFileExtensions,
}) => {
    const [fileFormData, setFileFormData] = useState(null);
    const [fileName, setFileName] = useState("");
    const [previewHeaders, setPreviewHeaders] = useState([]);
    const [previewRows, setPreviewRows] = useState([]);
    const [loadingUpload, setLoadingUpload] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState("");
    const [canImport, setCanImport] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    // Exclude fields that are not comparable or would cause false positives
    const exclude = [
        "__status",
        "Type", // names vs ids
        "Tags", // names vs ids; exclude unless you normalize both sides to comparable sets of arrays
        "ActiveStartDate", // formatted string vs stored timestamp; include only if you normalize both sides
        // Add "ItemLookupTags" here as well unless you normalize both sides to comparable sets
    ];

    // --- Helpers ---------------------------------------------------------------

    // Get the active/current version from an existing item (closest past activeStartDate)
    const getCurrentVersion = (versions = []) => {
        const now = new Date();
        return (
            versions
                ?.filter((v) => new Date(v.activeStartDate) <= now)
                .sort((a, b) => new Date(b.activeStartDate) - new Date(a.activeStartDate))[0] || null
        );
    };

    // Accepts array of {label, value} OR a JSON string; returns a stable JSON string
    const coerceValue = (v) => {
        if (v === "on") return true;           // your data shows "on"
        if (v === "true") return true;
        if (v === "false") return false;
        if (typeof v === "string" && v.trim() !== "" && !isNaN(v)) return Number(v);
        return v;
    };

    const normalizeAttrCollection = (input) => {
        if (input == null || input === "") return null;

        let arr = input;
        if (typeof input === "string") {
            try { arr = JSON.parse(input); } catch { return input; /* keep raw if not JSON */ }
        }

        if (!Array.isArray(arr)) return null;

        // {label, value} -> coerce types and sort by label, then stringify
        const normalized = arr
            .map(x => ({ label: String(x.label ?? "").trim(), value: coerceValue(x.value) }))
            .filter(x => x.label) // keep only with label
            .sort((a, b) => a.label.localeCompare(b.label));

        return JSON.stringify(normalized);
    };

    // Normalize the uploaded preview row (after keys were upper-cased) to a comparable shape
    const normalizeImportRow = (row) => {
        // Map various possible id keys
        const Id =
            row?.Id ??
            row?.id ??
            row?.ItemId ??
            row?.itemId ??
            row?.ItemLookupId ??
            row?.itemLookupId ??
            null;

        const v = {
            Id: Id,
            Name: row?.Name ?? row?.name ?? null,
            Options: row?.Options ?? row?.options ?? null,
            AutotaskCode: row?.AutotaskCode ?? row?.autotaskCode ?? null,
            UpfrontSalesforceCode: row?.UpfrontSalesforceCode ?? row?.upfrontSalesforceCode ?? null,
            UpfrontRecognitionMonths: row?.UpfrontRecognitionMonths ?? row?.upfrontRecognitionMonths ?? null,
            RecurringSalesforceCode: row?.RecurringSalesforceCode ?? row?.recurringSalesforceCode ?? null,

            // Names (not IDs) in the spreadsheet — excluded from equality by default
            Supplier: row?.Supplier ?? null,
            Type: row?.Type ?? null,

            // Root booleans/flags
            // Coerce booleans/flags
            IsInternal: coerceValue(row?.IsInternal ?? row?.isInternal ?? null),
            Disabled: coerceValue(row?.Disabled ?? row?.disabled ?? null),
            HideFromUsers: coerceValue(row?.HideFromUsers ?? row?.hideFromUsers ?? null),
            IsCreditItem: coerceValue(row?.IsCreditItem ?? row?.isCreditItem ?? null),
            ExemptFromMarginWarnings: coerceValue(row?.ExemptFromMarginWarnings ?? row?.exemptFromMarginWarnings ?? null),
            HideOnQuoteByDefault: coerceValue(row?.HideOnQuoteByDefault ?? row?.hideOnQuoteByDefault ?? null),


            Notes: row?.Notes ?? row?.notes ?? null,

            // Version-ish fields present in spreadsheet
            IsUpfront: coerceValue(row?.IsUpfront ?? null),
            IsRecurring: coerceValue(row?.IsRecurring ?? null),

            QuoteRecurringDescription: row?.QuoteRecurringDescription ?? null,
            RecurringCost: row?.RecurringCost ?? null,
            RecurringPrice: row?.RecurringPrice ?? null,
            RecurringIntervalMonths: row?.RecurringIntervalMonths ?? null,
            RecurringNumPayments: row?.RecurringNumPayments ?? null,
            RecurringStartMonth: row?.RecurringStartMonth ?? null,
            RecurringCostDiscountThreshold: row?.RecurringCostDiscountThreshold ?? null,
            RecurringCostDefaultDiscount: row?.RecurringCostDefaultDiscount ?? null,
            RecurringPriceDiscountThreshold: row?.RecurringPriceDiscountThreshold ?? null,
            RecurringPriceDefaultDiscount: row?.RecurringPriceDefaultDiscount ?? null,

            QuoteUpfrontDescription: row?.QuoteUpfrontDescription ?? null,
            UpfrontCost: row?.UpfrontCost ?? null,
            UpfrontPrice: row?.UpfrontPrice ?? null,
            UpfrontCostDiscountThreshold: row?.UpfrontCostDiscountThreshold ?? null,
            UpfrontCostDefaultDiscount: row?.UpfrontCostDefaultDiscount ?? null,
            UpfrontPriceDiscountThreshold: row?.UpfrontPriceDiscountThreshold ?? null,
            UpfrontPriceDefaultDiscount: row?.UpfrontPriceDefaultDiscount ?? null,

            CustomAttributes: normalizeAttrCollection(row?.CustomAttributes ?? row?.customAttributes),
            TypeCustomAttributes: normalizeAttrCollection(row?.TypeCustomAttributes ?? row?.typeCustomAttributes),

            // Dates in preview are often strings; excluded by default unless needed
            ActiveStartDate: row?.ActiveStartDate ?? null,
        };

        // Trim all strings
        Object.keys(v).forEach((k) => {
            if (typeof v[k] === "string") v[k] = v[k].trim();
        });

        return v;
    };

    // Normalize the existing DB item to the same comparable shape (pull from current version)
    const normalizeExistingItem = (item) => {
        const versions = item?.itemLookupVersions ?? item?.versions ?? [];
        const ver = getCurrentVersion(versions);
        const v = {
            Id: item?.id ?? null,
            Name: item?.name ?? null,
            Options: item?.options ?? null,
            AutotaskCode: item?.autotaskCode ?? null,
            UpfrontSalesforceCode: ver?.upfrontSalesforceCode ?? null,
            UpfrontRecognitionMonths: ver?.upfrontRecognitionMonths ?? null,
            RecurringSalesforceCode: ver?.recurringSalesforceCode ?? null,

            Supplier: supplierIdToName[item?.supplierId] ?? null,

            // We likely only have IDs for these (typeId/Tags), so exclude from compare to avoid false positives
            Tags: null,
            Type: null,

            IsInternal: item?.isInternal ?? null,
            Disabled: item?.disabled ?? null,
            HideFromUsers: item?.hideFromUsers ?? null,
            IsCreditItem: item?.isCreditItem ?? null,
            ExemptFromMarginWarnings: item?.exemptFromMarginWarnings ?? null,
            HideOnQuoteByDefault: item?.hideOnQuoteByDefault ?? null,
            Notes: item?.notes ?? null,

            IsUpfront: ver ? ver.isUpfront ?? null : null,
            IsRecurring: ver ? ver.isRecurring ?? null : null,

            QuoteRecurringDescription: ver ? ver.quoteRecurringDescription ?? null : null,
            RecurringCost: ver ? ver.recurringCost ?? null : null,
            RecurringPrice: ver ? ver.recurringPrice ?? null : null,
            RecurringIntervalMonths: ver ? ver.recurringIntervalMonths ?? null : null,
            RecurringNumPayments: ver ? ver.recurringNumPayments ?? null : null,
            RecurringStartMonth: ver ? ver.recurringStartMonth ?? null : null,
            RecurringCostDiscountThreshold: ver ? ver.recurringCostDiscountThreshold ?? null : null,
            RecurringCostDefaultDiscount: ver ? ver.recurringCostDefaultDiscount ?? null : null,
            RecurringPriceDiscountThreshold: ver ? ver.recurringPriceDiscountThreshold ?? null : null,
            RecurringPriceDefaultDiscount: ver ? ver.recurringPriceDefaultDiscount ?? null : null,

            QuoteUpfrontDescription: ver ? ver.quoteUpfrontDescription ?? null : null,
            UpfrontCost: ver ? ver.upfrontCost ?? null : null,
            UpfrontPrice: ver ? ver.upfrontPrice ?? null : null,
            UpfrontCostDiscountThreshold: ver ? ver.upfrontCostDiscountThreshold ?? null : null,
            UpfrontCostDefaultDiscount: ver ? ver.upfrontCostDefaultDiscount ?? null : null,
            UpfrontPriceDiscountThreshold: ver ? ver.upfrontPriceDiscountThreshold ?? null : null,
            UpfrontPriceDefaultDiscount: ver ? ver.upfrontPriceDefaultDiscount ?? null : null,

            CustomAttributes: normalizeAttrCollection(
                (ver && ver.customAttributes) ?? item?.customAttributes ?? null
            ),
            TypeCustomAttributes: normalizeAttrCollection(
                (ver && ver.typeCustomAttributes) ?? item?.typeCustomAttributes ?? null
            ),

            // Set to null to avoid format-based mismatches unless you need exact matching
            ActiveStartDate: null,
        };

        Object.keys(v).forEach((k) => {
            if (typeof v[k] === "string") v[k] = v[k].trim();
        });

        return v;
    };

    // Shallow field-by-field comparison (with logging)
    const diffObjects = (incoming, existing, exclude = []) => {
        const diffs = [];
        const keys = Object.keys(incoming).filter((k) => !exclude.includes(k));
        keys.forEach((k) => {
            const inc = incoming[k];
            const ex = existing[k];
            const changed = JSON.stringify(inc ?? null) !== JSON.stringify(ex ?? null);
            if (changed) {
                diffs.push({
                    key: k,
                    incoming: inc,
                    existing: ex,
                    typeofIncoming: typeof inc,
                    typeofExisting: typeof ex,
                });
            }
        });

        return diffs;
    };

    // --- Batch lookup of IDs from preview (robust + de-duped) -----------------

    const idsForBatch = useMemo(() => {
        if (!previewRows?.length) return [];
        const toNum = (v) => (typeof v === "string" ? v.trim() : v);

        const ids = previewRows
            .map(
                (r) =>
                    r?.Id ??
                    r?.id ??
                    r?.ItemId ??
                    r?.itemId ??
                    r?.ItemLookupId ??
                    r?.itemLookupId ??
                    null
            )
            .map((v) => toNum(v))
            .filter((v) => v !== undefined && v !== null && v !== "")
            .filter((v) => /^\d+$/.test(String(v)))
            .map((v) => Number(v));

        return Array.from(new Set(ids));
    }, [previewRows]);

    // Fetch existing items for batch IDs
    const {
        data: existingItems = [],
        isLoading: isLoadingExistingItems,
        error: existingItemsError,
    } = useQuery({
        queryKey: ["itemLookupBatch", idsForBatch],
        queryFn: () => fetchItemLookupBatch(idsForBatch),
        enabled: idsForBatch.length > 0 && !showValidation, // only check when previewing valid rows
        staleTime: 60_000,
    });

    // Fetch Suppliers
    const {
        data: suppliersData,
        isLoading: isLoadingSuppliers,
        error: suppliersError,
    } = useQuery({
        queryKey: ["itemLookupSuppliers"],
        queryFn: fetchSuppliers,
        staleTime: 60000
    });

    // Fetch Tags using useQuery
    const {
        data: tagsData,
        isLoading: isLoadingTags,
        error: tagsError,
    } = useQuery({
        queryKey: ["tags"],
        queryFn: fetchTags,
        staleTime: 60000
    });

    //fetch Item Lookup Types
    const {
        data: itemLookupTypes = [],
        isLoading: isLoadingItemLookupTypes,
        error: itemLookupTypesError,
    } = useQuery({
        queryKey: ["itemLookupTypes"],
        queryFn: fetchItemLookupType,
        staleTime: 60000
    });

    // Suppliers
    const supplierIdToName = useMemo(() => {
        if (!suppliersData) return {};
        return Object.fromEntries(suppliersData.map(s => [s.id, s.supplierName]));
    }, [suppliersData]);

    // const supplierNameToId = useMemo(() => {
    //     if (!suppliersData) return {};
    //     return Object.fromEntries(
    //         suppliersData
    //             .filter(s => typeof s.name === "string" && s.name.trim() !== "")
    //             .map(s => [s.name.trim().toLowerCase(), s.id])
    //     );
    // }, [suppliersData]);

    // --- UI handlers -----------------------------------------------------------
    const reset = () => {
        setFileFormData(null);
        setFileName("");
        setPreviewHeaders([]);
        setPreviewRows([]);
        setLoadingUpload(false);
        setImporting(false);
        setError("");
        setCanImport(false);
        setShowValidation(false);
    };

    const handleToggle = () => {
        reset();
        onClose?.();
    };

    // replace your extractHeaders with this version (note the boolean check)
    const extractHeaders = (data) => {
        if (!data?.length) return [];
        const valueMap = {};
        data.forEach((row) => {
            Object.entries(row).forEach(([key, val]) => {
                if (!valueMap[key]) valueMap[key] = [];
                valueMap[key].push(val);
            });
        });
        return Object.entries(valueMap)
            .filter(([, values]) =>
                values.some((v) =>
                    v !== null &&
                    v !== undefined &&
                    (typeof v === "number" || typeof v === "boolean" || (typeof v === "string" && v.trim() !== ""))
                )
            )
            .map(([key]) => key);
    };


    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        if (requiredFileExtensions) {
            const fileExtensions = requiredFileExtensions.split(",").map(ext => ext.trim().toLowerCase());
            if (!fileExtensions.some(ext => selected.name.toLowerCase().endsWith(ext))) {
                setError(`Only ${requiredFileExtensions} files are allowed.`);
                setFileFormData(null);
                setFileName("");
                setPreviewHeaders([]);
                setPreviewRows([]);
                setCanImport(false);
                setShowValidation(false);
                e.target.value = "";
                return;
            }
         }

        const fd = new FormData();
        fd.set("file", selected);
        setFileFormData(fd);
        setFileName(selected.name);
        setError("");
        setCanImport(false);
        setPreviewHeaders([]);
        setPreviewRows([]);
        setShowValidation(false);
        e.target.value = "";
    };

    // Fills version fields from unit fields based on billingType, without overwriting existing values
    const applyUnitToVersionFields = (row) => {
        const bt = (row.billingType || "").toString().trim().toLowerCase();
        const unitCost = row.unitCost;
        const unitPrice = row.unitPrice ?? row.price; // fall back to price if present

        const toNumberOrSame = (v) => {
            if (typeof v === "string" && v.trim() !== "" && !isNaN(v)) return Number(v);
            return v;
        };

        if (bt === "upfront") {
            if (row.upfrontCost == null && unitCost != null) row.upfrontCost = toNumberOrSame(unitCost);
            if (row.upfrontPrice == null && unitPrice != null) row.upfrontPrice = toNumberOrSame(unitPrice);
        } else if (bt === "recurring") {
            if (row.recurringCost == null && unitCost != null) row.recurringCost = toNumberOrSame(unitCost);
            if (row.recurringPrice == null && unitPrice != null) row.recurringPrice = toNumberOrSame(unitPrice);
        }
        // For other billing types (e.g., "Unassigned"), do nothing.
    };

    const normaliseRowsForPreview = (rows) => {
        rows.forEach((item) => {

            applyUnitToVersionFields(item);

            item.activeStartDate = item.activeStartDate ? formatDateTime(item.activeStartDate) : null;
            item.customAttributes = item.customAttributes
                ? JSON.stringify(item.customAttributes)
                : null;
            item.typeCustomAttributes = item.typeCustomAttributes
                ? JSON.stringify(item.typeCustomAttributes)
                : null;

            // KEEP item.id so Id is available after _.upperFirst
            delete item.row;
            delete item.validationErrors;
            delete item.itemLookupId;
            delete item.isImported;
        });

        // Upper-case first letter of keys to match your existing preview
        return rows.map((obj) =>
            Object.fromEntries(Object.entries(obj).map(([k, v]) => [_.upperFirst(k), v]))
        );
    };

    const handleUpload = async () => {
        if (!fileFormData) return;
        setLoadingUpload(true);
        setError("");
        setCanImport(false);
        setPreviewHeaders([]);
        setPreviewRows([]);
        setShowValidation(false);

        try {
            const response = await importItems(fileFormData);

            if (response?.status === 200) {
                const preview = normaliseRowsForPreview(response.data);
                if (Array.isArray(preview) && preview.length > 0) {
                    const headers = extractHeaders(preview);
                    setPreviewHeaders(headers);
                    setPreviewRows(preview);
                    setCanImport(true);
                } else {
                    setError("No data to display.");
                }
                return;
            }

            if (response?.status === 400) {
                // validation errors from API
                const validations = response.data.map((x) => ({
                    Tab: x.type,
                    Row: x.row,
                    ValidationErrors: x.validationErrors.join("\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0/\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0"),
                }));
                setPreviewHeaders(Object.keys(validations[0] || {}));
                setPreviewRows(validations);
                setShowValidation(true);
                setCanImport(false);
                return;
            }

            setError(() => {
                try {
                    return JSON.parse(response?.data)?.detail ?? "Upload or parsing failed.";
                } catch {
                    return "Upload or parsing failed.";
                }
            });
        } catch {
            setError("Upload or parsing failed.");
        } finally {
            setLoadingUpload(false);
        }
    };

    const removeNullOldValues = (audits) => {
        const transformed = {};
        for (const [key, value] of Object.entries(audits)) {
            transformed[key] = value?.old == null ? value?.new : value;
        }
        return transformed;
    };

    const handleImport = async () => {
        if (!fileFormData) return;
        setImporting(true);
        try {
            const result = await importAndSaveItems(fileFormData);

            for (const item of result) {
                item.changes = removeNullOldValues(item.changes);
                const action = item.isNewItem ? "Item created" : "Item changed";
                delete item.IsNewItem;

                await createAudit({
                    action,
                    result: "Successful",
                    notes: "Imported",
                    source: `item.${item.itemId}`,
                    payload: JSON.stringify(item.changes, null, 2),
                });
            }

            onImported?.();
        } finally {
            setImporting(false);
            handleToggle();
        }
    };

    const validationMode = useMemo(() => showValidation, [showValidation]);

    const flagCols = new Set([
        "IsInternal",
        "Disabled",
        "HideFromUsers",
        "ExemptFromMarginWarnings",
        "HideOnQuoteByDefault",
        "IsUpfront",
        "IsRecurring",
        "IsCreditItem"
    ]);

    // ---------- Build DataTable props ----------
    // Build columns from headers; add a Status column when not in validation mode
    const columns = useMemo(() => {
        const base = previewHeaders.map((h) => ({
            key: h,
            label: h,
            ...(flagCols.has(h) ? { render: (v) => (typeof v === "boolean" ? String(v) : v) } : {}),
        }));
        if (validationMode) return base;

        return [
            {
                key: "__status",
                label: "Status",
                render: (val) => {
                    if (val === "New") return <span className="text-success" title="New"><i className="bi bi-plus-circle-fill me-1" /></span>;
                    if (val === "Update") return <span title="Update"><i className="bi bi-pencil-fill me-1" /></span>;
                    return null;
                },
            },
            ...base,
        ];
    }, [previewHeaders, validationMode]);

    // Compare normalized import vs normalized existing; only mark Update when diffs exist
    const dataForTable = useMemo(() => {
        if (validationMode) return previewRows;

        const existingMap = new Map((existingItems || []).map((item) => [Number(item.id), item]));

        return previewRows.map((row, i) => {
            const idVal =
                row?.Id ??
                row?.id ??
                row?.ItemId ??
                row?.itemId ??
                row?.ItemLookupId ??
                row?.itemLookupId;
            const numericId = Number(idVal);
            const existingItem = Number.isFinite(numericId) ? existingMap.get(numericId) : undefined;

            if (!existingItem) {
                console.log(`[Row ${i}] NEW — no match for ID`, idVal);
                return { __status: "New", ...row };
            }

            const normImport = normalizeImportRow(row);
            const normExisting = normalizeExistingItem(existingItem);

            console.log(`[Row ${i}] Comparing normalized objects`, {
                id: numericId,
                normImport,
                normExisting,
            });

            const diffs = diffObjects(normImport, normExisting, exclude);

            if (diffs.length > 0) {
                console.log(`[Row ${i}] UPDATE — differences:`, diffs);
                console.log(`[Row ${i}] UPDATE keys:`, diffs.map(d => d.key));
                return { __status: "Update", ...row, __diffs: diffs };
            } else {
                console.log(`[Row ${i}] NO CHANGES — identical to existing`, { id: numericId });
                return { __status: "", ...row };
            }

        });
    }, [previewRows, validationMode, existingItems]);

    const importSummary = useMemo(() => {
        let newCount = 0;
        let updateCount = 0;
        let unchangedCount = 0;

        dataForTable.forEach(row => {
            if (row.__status === "New") newCount++;
            else if (row.__status === "Update") updateCount++;
            else unchangedCount++;
        });

        return { newCount, updateCount, unchangedCount };
    }, [dataForTable]);

    // Only show rows that are New or Update
    const visibleRows = useMemo(
        () => dataForTable.filter((r) => r.__status !== ""),
        [dataForTable]
    );

    const searchableFields = useMemo(
        () => columns.map((c) => c.key).filter((k) => k !== "__status"),
        [columns]
    );

    // stable helpers for table callbacks
    const normKey = useCallback((k) => String(k).toLowerCase(), []);

    const getRowClassName = useCallback((row) => {
        return row.__status === "New" ? "text-success fw-semibold" : "";
    }, []);

    const getCellClassName = useCallback((row, colKey) => {
        const changed =
            row.__status === "Update" &&
            row.__diffs?.some((d) => normKey(d.key) === normKey(colKey));
        if (row.__status === "New") return "text-success fw-semibold";
        if (changed) return "text-success fw-semibold";
        return "";
    }, [normKey]);

    // keep a stable sort key string
    const defaultSortByColumn = useMemo(
        () => (columns.length ? columns[0].key : ""),
        [columns]
    );

    // --- Render ----------------------------------------------------------------

    return (
        <>
            <Modal
                isOpen={isOpen}
                toggle={handleToggle}
                centered
                size="xl"
                scrollable
                backdrop="static"
                className="custom-modal-size"
            >
                <style>
                    {`
            .custom-modal-size { max-width: 95vw !important; width: 95vw !important; margin: 0 auto; }
            .custom-modal-size .modal-content { height: 90vh !important; }
          `}
                </style>

                <ModalHeader toggle={handleToggle}>{title}</ModalHeader>

                <ModalBody>
                    <Row className="align-items-center g-2">
                        <Col className="col-auto d-flex align-items-center gap-2">
                            <label
                                htmlFor="itemBulkFileInput"
                                className="btn"
                                style={{ backgroundColor: "white", color: "black", border: "1px solid #ced4da" }}
                            >
                                {fileName ? "Change file" : "Choose file"}
                            </label>
                            <Input
                                id="itemBulkFileInput"
                                type="file"
                                style={{ display: "none" }}
                                accept={requiredFileExtensions}
                                onChange={handleFileChange}
                            />
                            {fileName && (
                                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                                    <strong>{fileName}</strong>
                                </span>
                            )}
                        </Col>

                        {fileFormData && !loadingUpload && (
                            <Col className="col-auto">
                                <Button color="primary" onClick={handleUpload}>
                                    Upload
                                </Button>
                            </Col>
                        )}
                    </Row>

                    {error && (
                        <Alert color="danger" className="mt-3 d-flex justify-content-between align-items-start">
                            <span>{error}</span>
                            <Button outline color="dark" onClick={() => setError("")}>
                                x
                            </Button>
                        </Alert>
                    )}

                    {loadingUpload && (
                        <div className="text-center mt-3">
                            <Spinner color="primary" />
                            <p className="text-muted mt-2">Uploading and parsing...</p>
                        </div>
                    )}

                    {isLoadingExistingItems && previewRows.length > 0 && !validationMode && (
                        <div className="text-muted mt-2">Checking existing items…</div>
                    )}
                    {existingItemsError && !validationMode && (
                        <Alert color="warning" className="mt-2">
                            Couldn’t load existing items.
                        </Alert>
                    )}

                    {/* Use your DataTable for preview/validation display */}
                    {columns.length > 0 && dataForTable.length > 0 && (
                        <div className="mt-3">
                            <div className="mb-2 text-muted">
                                {importSummary.newCount} new,{" "}
                                {importSummary.updateCount} updated,{" "}
                                {importSummary.unchangedCount} unchanged (excluded from preview)
                                <br />
                                <small className="text-muted"> Changes to {exclude.join(", ")} are ignored in the preview but will be imported.</small>
                            </div>
                            <DataTable
                                columns={columns}
                                data={visibleRows}
                                isLoading={loadingUpload || (isLoadingExistingItems && !validationMode)}
                                error={false}
                                searchableFields={searchableFields}
                                paging
                                sortByColumn={defaultSortByColumn}
                                sortDirection="ascending"
                                getRowClassName={getRowClassName}
                                getCellClassName={getCellClassName}
                            />
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="primary" onClick={handleToggle}>
                        Cancel
                    </Button>
                    {!validationMode && (
                        <Button color="secondary" disabled={!canImport} onClick={handleImport}>
                            Import
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

            {/* Busy modal while persisting */}
            <Modal isOpen={importing} centered backdrop="static" keyboard={false}>
                <ModalBody
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "100px" }}
                >
                    <div className="text-center mt-3">
                        <Spinner color="primary" />
                        <p className="text-muted mt-2">Importing items, please wait...</p>
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
};

export default ItemBulkImport;
